package dev.lingvo.site.api

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.AuthResponse
import dev.lingvo.shared.MeResponse
import dev.lingvo.shared.Role
import dev.lingvo.shared.ServerFeaturesDto
import dev.lingvo.shared.UserDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.i18n.tr
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException

sealed interface SessionState {
    data object Loading : SessionState
    data object LoggedOut : SessionState
    data class Authenticated(
        val user: UserDto,
        val organizations: List<WorkspaceDto> = emptyList(),
        val serverFeatures: ServerFeaturesDto = ServerFeaturesDto(),
    ) : SessionState

    data class Failure(val message: String) : SessionState
}

/** Opaque request/cache identity for one authenticated session generation. */
data class SessionRequestIdentity(
    val userId: String,
    val generation: Long,
)

object Session {
    private var storedToken: String? = readStoredToken()
    private var sessionGeneration = 0L
    private var authenticatedGeneration = 0L
    private var loadingGeneration: Long? = null
    private var sessionCheckAttempt = 0L

    var state by mutableStateOf<SessionState>(
        if (storedToken == null) SessionState.LoggedOut else SessionState.Loading,
    )
        private set

    val token: String?
        get() = storedToken

    val user: UserDto?
        get() = (state as? SessionState.Authenticated)?.user

    val organizations: List<WorkspaceDto>
        get() = (state as? SessionState.Authenticated)?.organizations.orEmpty()

    val serverFeatures: ServerFeaturesDto
        get() = (state as? SessionState.Authenticated)?.serverFeatures ?: ServerFeaturesDto()

    /**
     * Changes on every accepted `/auth/me` publication and logout, even when the same token or user
     * returns. Request caches must include this key so refreshed memberships/features and an
     * earlier browser session can never publish into a later authenticated snapshot.
     */
    val requestIdentity: SessionRequestIdentity?
        get() = (state as? SessionState.Authenticated)?.let { authenticated ->
            SessionRequestIdentity(authenticated.user.id, authenticatedGeneration)
        }

    val isAuthenticated: Boolean
        get() = state is SessionState.Authenticated

    /**
     * The role this person holds in the workspace they are looking at.
     *
     * Null while the session is loading and for a workspace they do not belong to, and both mean the
     * same thing to the navigation: show nothing role-gated. Guessing OWNER until proven otherwise
     * would flash an owner's menu at a translator on every page load.
     */
    fun roleIn(workspaceSlug: String?): Role? =
        organizations.firstOrNull { it.slug == workspaceSlug }?.role

    /** The same answer, for the surfaces that hold a workspace id rather than its slug. */
    fun roleInOrg(orgId: String?): Role? =
        organizations.firstOrNull { it.id == orgId }?.role

    /** The slug of a workspace this person belongs to, for surfaces that hold its id. */
    fun slugForOrg(orgId: String?): String? =
        organizations.firstOrNull { it.id == orgId }?.slug

    /**
     * The role to draw the shell with when no workspace is named — on `/app`, or on a settings page
     * that belongs to the person rather than to a project.
     *
     * The strongest role held anywhere, because the shell's job here is to show the destinations
     * that exist for this person at all. Every one of them re-checks on arrival; this only decides
     * whether the door is drawn.
     */
    fun strongestRole(): Role? = organizations.minByOrNull { it.role.ordinal }?.role

    suspend fun login(email: String, password: String): AuthResponse =
        establishSession { ApiClient.login(email, password) }

    suspend fun register(email: String, password: String, name: String, acquisitionSource: String? = null): AuthResponse =
        establishSession { ApiClient.register(email, password, name, acquisitionSource) }

    fun logout() {
        storedToken = null
        sessionGeneration++
        sessionCheckAttempt++
        authenticatedGeneration++
        runCatching { window.localStorage.removeItem(TOKEN_STORAGE_KEY) }
        state = SessionState.LoggedOut
    }

    suspend fun loadMe(): MeResponse? {
        val tokenSnapshot = storedToken
        val generationSnapshot = sessionGeneration
        if (loadingGeneration == generationSnapshot) return null
        if (tokenSnapshot.isNullOrBlank()) {
            state = SessionState.LoggedOut
            return null
        }

        val attemptSnapshot = ++sessionCheckAttempt
        loadingGeneration = generationSnapshot
        // **Only when there is nothing to keep**, which is the same rule `loadReloadable` follows
        // one level down. `AuthGuard` renders the app-shell skeleton for `Loading`, so re-reading
        // `/auth/me` while a page is on screen tore that page down and rebuilt it — and the callers
        // that do this are refreshing memberships right after the reader created or left a
        // workspace, so the action they just took blanked the surface they took it on. An initial
        // load has nothing to preserve and still announces itself.
        if (state !is SessionState.Authenticated) state = SessionState.Loading
        return try {
            ApiClient.me().also { response ->
                if (isCurrentSession(tokenSnapshot, generationSnapshot, attemptSnapshot)) {
                    authenticatedGeneration++
                    state = SessionState.Authenticated(
                        user = response.user,
                        organizations = response.organizations,
                        serverFeatures = response.serverFeatures,
                    )
                }
            }
        } catch (failure: ApiException) {
            if (isCurrentSession(tokenSnapshot, generationSnapshot, attemptSnapshot)) {
                if (failure.status == 401) {
                    logout()
                } else {
                    state = SessionState.Failure(sessionCheckFailureMessage())
                }
            }
            null
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (isCurrentSession(tokenSnapshot, generationSnapshot, attemptSnapshot)) {
                state = SessionState.Failure(sessionCheckFailureMessage())
            }
            null
        } finally {
            if (loadingGeneration == generationSnapshot) loadingGeneration = null
        }
    }

    /**
     * Adopts the session behind a one-time OAuth code.
     *
     * Routed through [establishSession] like login and register so the token is persisted, the
     * generation bumped and `/auth/me` loaded by exactly the same code — three sign-in paths that
     * each stored the token their own way is how one of them ends up not clearing the old session.
     */
    suspend fun adoptOAuthSession(code: String): AuthResponse =
        establishSession { ApiClient.exchangeOAuthCode(code) }

    private suspend fun establishSession(request: suspend () -> AuthResponse): AuthResponse {
        val response = request()
        persistToken(response.token)
        state = SessionState.Loading
        loadMe()
        return response
    }

    private fun persistToken(token: String) {
        try {
            window.localStorage.setItem(TOKEN_STORAGE_KEY, token)
        } catch (failure: Throwable) {
            throw ApiException(
                status = 0,
                code = "storage_unavailable",
                message = failure.message?.takeIf(String::isNotBlank)
                    ?: "Your browser couldn't save the login session.",
            )
        }
        storedToken = token
        sessionGeneration++
    }

    private fun isCurrentSession(token: String, generation: Long, attempt: Long): Boolean =
        storedToken == token && sessionGeneration == generation && sessionCheckAttempt == attempt

    private fun sessionCheckFailureMessage(): String = tr(
        "ds.api.session.couldnt_verify_your_saved_session",
        "We couldn't verify your saved session. Check your connection and retry.",
    )

    private fun readStoredToken(): String? =
        runCatching { window.localStorage.getItem(TOKEN_STORAGE_KEY) }
            .getOrNull()
            ?.takeIf(String::isNotBlank)

    private const val TOKEN_STORAGE_KEY = "lingvo:token"
}

@Composable
fun rememberSession(): SessionState {
    val state = Session.state
    LaunchedEffect(Unit) {
        if (state == SessionState.Loading) Session.loadMe()
    }
    return state
}
